/** SUNTRONIX ONE — Process Guidance panel.
 *  Reads the ACTIVE Process Studio process for a module + record and
 *  renders CURRENT / NEXT / REQUIRED BEFORE NEXT.  Read-only.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { GitBranch, CheckCircle2, XCircle, Settings } from "lucide-react";
import { AdminChip } from "@/components/app/AdminMode";

export default function ProcessGuidancePanel({ module, recordId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!recordId) return;
    api.get(`/process-studio/guidance/${module}/${recordId}`)
        .then(({ data }) => setData(data))
        .catch(() => setData(null));
  }, [module, recordId]);
  if (!data) return null;

  if (!data.process) {
    return (
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-[11px] text-slate-600"
          data-testid="process-guidance-empty">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <GitBranch className="h-3.5 w-3.5" /> No governed process configured for <b>{module}</b>.
          </div>
          <AdminChip area="modules" label="Configure Process"
              testid="admin-chip-configure-process" />
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="process-guidance">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
          <GitBranch className="h-3.5 w-3.5 text-violet-800" /> Process guidance
          <span className="ml-2 text-[9px] font-mono text-slate-500">
            {data.process.name} · v{data.process.version} · {data.process.status}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/administration/process-studio?module=${module}&id=${data.process.id}`}
              data-testid="process-guidance-open"
              className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">
            Open in Process Studio
          </Link>
          <AdminChip area="modules" label="Configure Process"
              testid="admin-chip-guidance-configure" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
        <div className="border border-slate-200 rounded p-2">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Current</div>
          <div className="text-[13px] font-semibold text-slate-900">
            {data.current?.label || "—"}
          </div>
          <div className="text-[9px] font-mono text-slate-500">{data.current?.id}</div>
        </div>
        <div className="md:col-span-2 border border-slate-200 rounded p-2">
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">
            Available next actions
          </div>
          {(data.next_actions || []).length === 0 ? (
            <div className="text-slate-400">No transitions available from this stage.</div>
          ) : (
            <div className="space-y-1">
              {data.next_actions.map((n) => (
                <div key={n.transition} className="border border-slate-100 rounded p-1.5"
                    data-testid={`guidance-next-${n.transition}`}>
                  <div className="flex items-center gap-2">
                    <span className={"text-[9px] px-1.5 py-0.5 rounded ring-1 " +
                        (n.ready ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                                            : "bg-amber-50 text-amber-800 ring-amber-200")}>
                      {n.ready ? "READY" : "REQUIRED"}
                    </span>
                    <span className="font-semibold flex-1">{n.label}</span>
                    <span className="font-mono text-[9px] text-slate-500">→ {n.to_stage}</span>
                  </div>
                  {(n.required || []).length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {n.required.map((r, i) => (
                        <li key={i} className="flex items-center gap-1 text-[10px]">
                          {r.ok
                            ? <CheckCircle2 className="h-3 w-3 text-emerald-700" />
                            : <XCircle className="h-3 w-3 text-rose-600" />}
                          <span className={r.ok ? "text-slate-600" : "text-rose-700"}>
                            {r.field}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(n.blockers || []).length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {n.blockers.map((b, i) => (
                        <li key={i} className="flex items-center gap-1 text-[10px] text-rose-700">
                          <XCircle className="h-3 w-3" />
                          {b.label}
                        </li>
                      ))}
                    </ul>
                  )}
                  {n.approval_required && (
                    <div className="mt-1 text-[10px] text-amber-700">
                      Requires approval before transition.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
